import { useForm } from 'react-hook-form';
import { Alert, Box, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import { Button, Input } from 'components';
import { yup } from 'utils';
import { emailRegex } from 'utils/regex';

type PayloadType = { email: string };
/* eslint-disable no-unused-vars */
type FormType = {
  onButton: (data: PayloadType) => void;
  counter: number;
  isSubmitting?: boolean;
};
const Form: React.FC<FormType> = ({
  onButton,
  counter,
  isSubmitting = false,
}: FormType) => {
  const { t } = useTranslation('component.auth.forgotPassword.form');

  const schema = yup.object().shape({
    email: yup
      .string()
      .email()
      .lowercase()
      .matches(emailRegex, t('email_validate'))
      .label('email')
      .required(),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: PayloadType) => {
    onButton(data);
  };

  const formProps = useInputAttributes(register, errors, {
    email: t('email'),
  });

  const resendButton = `${t('resend')} ( ${counter.toString()} )`;

  const disable = counter !== 0;

  const fieldSurface = {
    background: 'secondary',
    borderWidth: '1px',
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 'lg',
    _hover: { background: 'rgba(255,255,255,0.05)' },
    _focus: {
      background: 'secondary',
      borderColor: 'primary',
    },
  };

  return (
    <Box w="100%">
      {disable && (
        <Alert.Root
          status="info"
          mb={5}
          borderRadius="md"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.12)"
        >
          <Alert.Indicator />
          <Text color="info">{t('codeSent')}</Text>
        </Alert.Root>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={5}>
          <Input
            {...formProps.email}
            w="100%"
            disabled={disable || isSubmitting}
            {...fieldSurface}
          />
        </Box>
        <Button
          type="submit"
          variant="filled"
          display="block"
          title={disable ? resendButton : t('submit')}
          isDisabled={disable || isSubmitting}
          isLoading={isSubmitting}
          minW="100%"
          mt={8}
        />
      </form>
    </Box>
  );
};

export default Form;
